'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';

export interface LocaleConfig {
  currency: string;
  currencySymbol: string;
  country: string;
  countryCode: string;
  language: 'ar' | 'en';
  dir: 'rtl' | 'ltr';
  cities: string[];
  cityLabel: string; // "All cities" label in the right language
}

const UAE_CITIES = ['الكل', 'دبي', 'أبوظبي', 'الشارقة', 'عجمان', 'رأس الخيمة', 'الفجيرة', 'أم القيوين'];
const SA_CITIES = ['الكل', 'الرياض', 'جدة', 'مكة المكرمة', 'المدينة المنورة', 'الدمام', 'الخبر', 'أبها', 'تبوك'];
const KW_CITIES = ['الكل', 'مدينة الكويت', 'حولي', 'الفروانية', 'الأحمدي', 'الجهراء', 'مبارك الكبير'];
const QA_CITIES = ['الكل', 'الدوحة', 'الريان', 'الوكرة', 'الخور', 'أم صلال', 'الشمال'];
const BH_CITIES = ['الكل', 'المنامة', 'المحرق', 'الرفاع', 'مدينة حمد', 'عيسى', 'سترة'];
const OM_CITIES = ['الكل', 'مسقط', 'صلالة', 'نزوى', 'صحار', 'صور', 'البريمي'];
const EG_CITIES = ['الكل', 'القاهرة', 'الإسكندرية', 'الجيزة', 'الشروق', 'التجمع الخامس', 'العاشر من رمضان'];
const JO_CITIES = ['الكل', 'عمّان', 'الزرقاء', 'إربد', 'العقبة', 'السلط', 'مادبا'];
const US_CITIES = ['All', 'New York', 'Los Angeles', 'Chicago', 'Houston', 'Miami', 'Dallas', 'San Francisco'];
const GB_CITIES = ['All', 'London', 'Manchester', 'Birmingham', 'Leeds', 'Glasgow', 'Liverpool'];
const EU_CITIES = ['All', 'Paris', 'Berlin', 'Madrid', 'Rome', 'Amsterdam', 'Brussels'];
const DEFAULT_CITIES = ['الكل', 'دبي', 'أبوظبي', 'الشارقة', 'عجمان', 'رأس الخيمة'];

export const DEFAULT_LOCALE: LocaleConfig = {
  currency: 'AED',
  currencySymbol: 'د.إ',
  country: 'الإمارات العربية المتحدة',
  countryCode: 'AE',
  language: 'ar',
  dir: 'rtl',
  cities: UAE_CITIES,
  cityLabel: 'الكل',
};

export const LOCALE_MAP: Record<string, LocaleConfig> = {
  AE: { currency: 'AED', currencySymbol: 'د.إ', country: 'الإمارات العربية المتحدة', countryCode: 'AE', language: 'ar', dir: 'rtl', cities: UAE_CITIES, cityLabel: 'الكل' },
  SA: { currency: 'SAR', currencySymbol: 'ر.س', country: 'المملكة العربية السعودية', countryCode: 'SA', language: 'ar', dir: 'rtl', cities: SA_CITIES, cityLabel: 'الكل' },
  KW: { currency: 'KWD', currencySymbol: 'د.ك', country: 'الكويت', countryCode: 'KW', language: 'ar', dir: 'rtl', cities: KW_CITIES, cityLabel: 'الكل' },
  QA: { currency: 'QAR', currencySymbol: 'ر.ق', country: 'قطر', countryCode: 'QA', language: 'ar', dir: 'rtl', cities: QA_CITIES, cityLabel: 'الكل' },
  BH: { currency: 'BHD', currencySymbol: 'د.ب', country: 'البحرين', countryCode: 'BH', language: 'ar', dir: 'rtl', cities: BH_CITIES, cityLabel: 'الكل' },
  OM: { currency: 'OMR', currencySymbol: 'ر.ع', country: 'عُمان', countryCode: 'OM', language: 'ar', dir: 'rtl', cities: OM_CITIES, cityLabel: 'الكل' },
  EG: { currency: 'EGP', currencySymbol: 'ج.م', country: 'مصر', countryCode: 'EG', language: 'ar', dir: 'rtl', cities: EG_CITIES, cityLabel: 'الكل' },
  JO: { currency: 'JOD', currencySymbol: 'د.أ', country: 'الأردن', countryCode: 'JO', language: 'ar', dir: 'rtl', cities: JO_CITIES, cityLabel: 'الكل' },
  LB: { currency: 'USD', currencySymbol: '$', country: 'لبنان', countryCode: 'LB', language: 'ar', dir: 'rtl', cities: DEFAULT_CITIES, cityLabel: 'الكل' },
  US: { currency: 'USD', currencySymbol: '$', country: 'United States', countryCode: 'US', language: 'en', dir: 'ltr', cities: US_CITIES, cityLabel: 'All' },
  GB: { currency: 'GBP', currencySymbol: '£', country: 'United Kingdom', countryCode: 'GB', language: 'en', dir: 'ltr', cities: GB_CITIES, cityLabel: 'All' },
  EU: { currency: 'EUR', currencySymbol: '€', country: 'Europe', countryCode: 'EU', language: 'en', dir: 'ltr', cities: EU_CITIES, cityLabel: 'All' },
};

interface LocaleContextValue {
  locale: LocaleConfig;
  setLocale: (locale: LocaleConfig) => void;
  isDetecting: boolean;
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  isDetecting: true,
});

export const useLocale = () => useContext(LocaleContext);

export function formatPriceWithLocale(price: number, locale: LocaleConfig): string {
  if (price >= 1000000) {
    return `${(price / 1000000).toFixed(2)} م ${locale.currencySymbol}`;
  }
  if (price >= 1000) {
    return `${(price / 1000).toFixed(0)}ك ${locale.currencySymbol}`;
  }
  return `${price.toLocaleString(locale.language === 'ar' ? 'ar-SA' : 'en-US')} ${locale.currencySymbol}`;
}

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<LocaleConfig>(DEFAULT_LOCALE);
  const [isDetecting, setIsDetecting] = useState(true);

  const setLocale = (newLocale: LocaleConfig) => {
    setLocaleState(newLocale);
    // Persist manual selection
    try {
      localStorage.setItem('aqar_locale', newLocale.countryCode);
    } catch {}
  };

  useEffect(() => {
    const detectLocale = async () => {
      // Check if user manually selected a locale before
      try {
        const saved = localStorage.getItem('aqar_locale');
        if (saved && LOCALE_MAP[saved]) {
          setLocaleState(LOCALE_MAP[saved]);
          setIsDetecting(false);
          return;
        }
      } catch {}

      try {
        // Try to detect country via IP geolocation
        const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(4000) });
        if (res.ok) {
          const data = await res.json();
          const countryCode = data.country_code as string;
          const detected = LOCALE_MAP[countryCode];
          if (detected) {
            setLocaleState(detected);
            setIsDetecting(false);
            return;
          }
          // Check if it's a European country
          const euCountries = ['DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'AT', 'PT', 'PL', 'SE', 'DK', 'FI', 'NO', 'CH'];
          if (euCountries.includes(countryCode)) {
            setLocaleState(LOCALE_MAP['EU']);
            setIsDetecting(false);
            return;
          }
        }
      } catch {
        // Fallback: try browser language
        const lang = navigator.language?.toLowerCase() || 'ar';
        if (lang.startsWith('ar-sa')) {
          setLocaleState(LOCALE_MAP['SA']);
        } else if (lang.startsWith('ar-kw')) {
          setLocaleState(LOCALE_MAP['KW']);
        } else if (lang.startsWith('ar-qa')) {
          setLocaleState(LOCALE_MAP['QA']);
        } else if (lang.startsWith('ar')) {
          setLocaleState(DEFAULT_LOCALE); // UAE default for Arabic
        } else if (lang.startsWith('en-gb')) {
          setLocaleState(LOCALE_MAP['GB']);
        } else if (lang.startsWith('en')) {
          setLocaleState(LOCALE_MAP['US']);
        }
      }

      setIsDetecting(false);
    };

    detectLocale();
  }, []);

  // Apply dir to document
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.dir = locale.dir;
      document.documentElement.lang = locale.language;
    }
  }, [locale.dir, locale.language]);

  return (
    <LocaleContext.Provider value={{ locale, setLocale, isDetecting }}>
      {children}
    </LocaleContext.Provider>
  );
}
